import type { ChatSourceLink } from "@gb/contracts";
import { formatBoxParam } from "@/features/brain/region-overlay";

/**
 * Turning a cited source into somewhere to go.
 *
 * The client-side counterpart of `formatCitation()`, and it exists for the same
 * reason: one renderer, so every surface that shows a citation sends the reader
 * to the same place. The API deliberately hands over a discriminated union
 * rather than a URL — it does not know this app's routes, and the mobile client's
 * are different again — so this is where the union becomes a link.
 *
 * The `switch` is exhaustive by construction: `never` in the default arm means
 * adding a link kind to the contract fails the build here rather than silently
 * rendering nothing at runtime.
 */

/**
 * Every link is an in-app route.
 *
 * There used to be an `external` flag here, for the one case that pointed at
 * the presigned-download route and had to be a browser navigation. That case
 * is gone — a document citation now opens the viewer — and with it the flag,
 * which was `false` on every branch and would have misled the next reader into
 * thinking some other value was reachable.
 */
export type ResolvedLink = { href: string };

export function linkFor(projectId: string, link: ChatSourceLink): ResolvedLink {
  switch (link.kind) {
    case "brain-entry":
      return { href: `/projects/${projectId}/brain/${link.entryId}` };
    case "journal":
      return { href: `/projects/${projectId}/journal/${link.entryId}` };
    case "journal-note":
      // The dated feed, not a row route: a note is never a page of its own, and
      // an unflagged one has no `journal_entries` row behind it to open. The
      // Journal screen reads `?date` and shows that day.
      return { href: `/projects/${projectId}/journal?date=${link.date}` };
    case "crew":
      // The Crew screen reads `?person` and opens that person's drawer, so the
      // link lands on the record rather than on the roster with the reader left
      // to find the name.
      return {
        href: `/projects/${projectId}/crew?person=${encodeURIComponent(link.personId)}`,
      };
    case "document": {
      // The **viewer**, not the download route. This used to point at
      // `/documents/:id/download` as an external navigation, and that could not
      // work: the route is behind `AuthGuard`, which reads the bearer token from
      // a header, and a top-level navigation sends none — so it answered 401.
      // The `#page=N` was inert too, since that route serves `attachment` and a
      // downloaded file has no viewer to honour the fragment.
      //
      // The viewer is an ordinary in-app route, so it carries the session, and
      // `?page=` is the same parameter a Brain search hit uses — it opens the
      // PDF at that page and scrolls the extracted text to it.
      //
      // `&box=` is the region a look read on that page, or on a picture file
      // (2026-09-26, Phase 5 slice V). The viewer draws it; a viewer that does
      // not know it still opens at the page. Its commas are left as written:
      // a query allows them, and they keep the address readable.
      const query = [
        link.page ? `page=${link.page}` : null,
        link.box ? `box=${formatBoxParam(link.box)}` : null,
      ].filter((part): part is string => part !== null);
      const href = `/projects/${projectId}/brain/files/${link.documentId}`;
      return { href: query.length > 0 ? `${href}?${query.join("&")}` : href };
    }
    default: {
      const exhaustive: never = link;
      return exhaustive;
    }
  }
}
